import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { stageRig } from '../lighting/stage-light-rig.js';

// Shared-rig contract (see the framing addendum): this visualizer's
// declared relationship to the one shared StageLightRig instance, and the
// beam intensity it asks for. Most restrictive wins across owners.
const RIG = 'optional';
const RIG_OWNER = 'spotwall';
const RIG_INTENSITY_SCALE = 1;
const RIG_ZONES = 'default';
// Same lit-floor treatment as neon-pulse.js/kinetic-rig.js: relaxes the rig's
// spot falloff (decay 2.4 -> 1.1) so beams actually reach the ground and draw
// pools on it instead of arriving divided by ~170.
const RIG_FLOOR_LIGHT_BOOST = 3;
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'spotwall',
  family: 'grid',
  energyLevel: 0.85,
  bpmMin: 112,
  bpmMax: 185,
  movement: 0.85,
  danceability: 0.80,
  tonality: 0.85,
  enabled: true
};

// SpotWall: a grid of round spots facing the camera dead-on, each one
// independently fading in/out on its own random on/off cycle - a wall of
// bulbs, not beams. The rig is included here (unlike this visualizer's
// first version) specifically so both can be previewed together; nothing
// about the wall itself depends on it.
//
// Per-spot flicker timing (on-duration/off-duration/phase) is computed
// once per spot at init and baked into vertex attributes, then evaluated
// entirely in the vertex shader from uTime - same reasoning as
// StormLayer's GPU-side beam-proximity check: doing this per-spot in JS
// every frame would be COLS*ROWS scalar ops/frame for no benefit, since
// it's simple deterministic math either way.

// Grid density (world-unit spacing) rather than a fixed column/row count -
// count is derived at init from the camera's actual fov/aspect so the
// wall always fills the frame edge to edge regardless of screen shape,
// instead of covering a fixed area that happened to fit one aspect ratio.
const SPOT_SPACING = 0.85;
const COVERAGE = 0.95; // fraction of the visible frustum to fill - a small margin avoids edge spots clipping off-screen
const CAMERA_DISTANCE = 7.2;
const WALL_CENTER_Y = 2.0; // matches where the rig's beams typically converge, so both read as one scene
const FADE_S = 0.3; // shared fade-in/fade-out duration, in seconds
const BASE_DIM = 0.07; // faint always-on presence, like an unlit bulb catching ambient light
const POINT_WORLD_SIZE = 0.5; // was 0.34 - "augmente le diametre de chaque spot" (spacing left untouched)
const INTENSITY_BOOST = 1.6; // "plus intenses" - pushes lit spots past the bloom threshold harder
// Beat-locked flash (see beat-pulse.js) on top of each spot's own
// independent on/off flicker - a synchronized brighten across whichever
// spots happen to be lit right on the beat.
const BEAT_PULSE_AMOUNT = 0.4;
const BEAT_PULSE_DECAY = 9;

// Fallback per-spot palette (no artwork palette resolved yet) - warm
// gold to deep amber. Once a real palette is available, each spot takes
// one of the album's actual vivid colors instead (see _recomputeColors) -
// this yellow band only carries the look before that first resolves, or
// for a track with no artwork at all.
const HUE_MIN = 0.1; // ~36deg
const HUE_MAX = 0.16; // ~58deg
const SAT_MIN = 0.55;
const SAT_MAX = 0.95;
const LIGHT_MIN = 0.45;
const LIGHT_MAX = 0.7;

// How much each spot's saturation/lightness is allowed to jitter away
// from its assigned vivid color, using JITTER_SEED (below) as the
// per-spot random source - texture across the wall instead of every spot
// sharing one of only ~5 flat, identical colors.
const VIVID_JITTER_AMOUNT = 0.3;


const vertexShader = `
  uniform float uTime;
  uniform float uFade;
  uniform float uSizeScale;
  attribute float aOnDuration;
  attribute float aOffDuration;
  attribute float aPhase;
  attribute vec3 aColor;
  varying float vBrightness;
  varying vec3 vColor;

  void main() {
    float cycle = aOnDuration + aOffDuration;
    float t = mod(uTime + aPhase, cycle);
    float fadeIn = smoothstep(0.0, uFade, t);
    float fadeOut = 1.0 - smoothstep(aOnDuration - uFade, aOnDuration, t);
    vBrightness = min(fadeIn, fadeOut);
    vColor = aColor;

    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = ${POINT_WORLD_SIZE.toFixed(3)} * uSizeScale / -mvPosition.z;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const fragmentShader = `
  uniform float uBaseDim;
  uniform float uIntensity;
  varying float vBrightness;
  varying vec3 vColor;

  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv) * 2.0;
    if (d > 1.0) discard;
    float edge = pow(1.0 - d, 1.6);

    float brightness = uBaseDim + vBrightness * uIntensity;
    gl_FragColor = vec4(vColor * brightness, edge * (uBaseDim * 0.6 + vBrightness));
  }
`;

export class SpotWallVisualizer extends Visualizer {
  // options.includeRig (default true) - the standalone/debug-switcher use
  // of this visualizer keeps its own rig by default (unchanged), but the
  // orchestrator (adaptive-party.js) constructs it with includeRig:false
  // when SpotWall is the chosen visual, since a full rig on top of a
  // full-screen spot grid read as "trop" in that context.
  constructor(options) {
    super();
    this._includeRig = !options || options.includeRig !== false;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    camera.position.set(0, WALL_CENTER_Y + 0.5, CAMERA_DISTANCE);
    camera.lookAt(0, WALL_CENTER_Y, 0);

    if (this._includeRig) {
      this._rig = stageRig.acquire(RIG_OWNER, {
        scene: scene,
        intensityScale: RIG_INTENSITY_SCALE,
        zones: RIG_ZONES,
        floorLightBoost: RIG_FLOOR_LIGHT_BOOST
      });
    }

    // Visible frustum size at the wall's depth, so the grid always spans
    // the actual screen edge to edge regardless of aspect ratio/fov,
    // rather than a fixed column/row count tuned for one screen shape.
    const vFovRad = THREE.MathUtils.degToRad(camera.fov);
    const halfHeight = CAMERA_DISTANCE * Math.tan(vFovRad / 2);
    const halfWidth = halfHeight * camera.aspect;
    const visibleWidth = halfWidth * 2 * COVERAGE;
    const visibleHeight = halfHeight * 2 * COVERAGE;
    const cols = Math.max(2, Math.round(visibleWidth / SPOT_SPACING) + 1);
    const rows = Math.max(2, Math.round(visibleHeight / SPOT_SPACING) + 1);
    const wallWidth = (cols - 1) * SPOT_SPACING;
    const wallHeight = (rows - 1) * SPOT_SPACING;

    const count = cols * rows;
    this._count = count;
    const positions = new Float32Array(count * 3);
    const onDurations = new Float32Array(count);
    const offDurations = new Float32Array(count);
    const phases = new Float32Array(count);
    const colors = new Float32Array(count * 3);
    // Each spot's own random HSL, generated once - the fallback "identity"
    // color used until a real artwork palette resolves (see
    // _recomputeColors), never overwritten.
    this._baseHue = new Float32Array(count);
    this._baseSat = new Float32Array(count);
    this._baseLight = new Float32Array(count);
    // A random vivid-color slot (0..4) plus a jitter seed per spot,
    // generated once - which of the album's ~5 vivid colors this spot
    // takes, and how far its saturation/lightness drifts from that
    // color's own, so the wall reads as "textured with the album's
    // palette" instead of ~5 flat color clusters.
    this._vividSlot = new Uint8Array(count);
    this._jitterSeed = new Float32Array(count);
    // Fixed 50/50 split, decided once per spot at init - half the wall
    // always stays yellow (the original "des tonalites de jaune" look)
    // regardless of the cover, half takes the album's real vivid colors -
    // a stable per-spot assignment, not re-rolled on every track change.
    this._usesAlbumColor = new Uint8Array(count);
    const tmpColor = new THREE.Color();

    let i = 0;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        positions[i * 3] = col * SPOT_SPACING - wallWidth / 2;
        positions[i * 3 + 1] = row * SPOT_SPACING - wallHeight / 2 + WALL_CENTER_Y;
        positions[i * 3 + 2] = 0;

        const onDuration = 0.6 + Math.random() * 1.8;
        const offDuration = 1.0 + Math.random() * 3.0;
        onDurations[i] = onDuration;
        offDurations[i] = offDuration;
        phases[i] = Math.random() * (onDuration + offDuration);

        const hue = HUE_MIN + Math.random() * (HUE_MAX - HUE_MIN);
        const sat = SAT_MIN + Math.random() * (SAT_MAX - SAT_MIN);
        const light = LIGHT_MIN + Math.random() * (LIGHT_MAX - LIGHT_MIN);
        this._baseHue[i] = hue;
        this._baseSat[i] = sat;
        this._baseLight[i] = light;
        this._vividSlot[i] = Math.floor(Math.random() * 5);
        this._jitterSeed[i] = Math.random();
        this._usesAlbumColor[i] = Math.random() < 0.5 ? 1 : 0;
        tmpColor.setHSL(hue, sat, light);
        colors[i * 3] = tmpColor.r;
        colors[i * 3 + 1] = tmpColor.g;
        colors[i * 3 + 2] = tmpColor.b;

        i++;
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aOnDuration', new THREE.BufferAttribute(onDurations, 1));
    geometry.setAttribute('aOffDuration', new THREE.BufferAttribute(offDurations, 1));
    geometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));
    geometry.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));

    this._material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uFade: { value: FADE_S },
        uSizeScale: { value: 1 },
        uBaseDim: { value: BASE_DIM },
        uIntensity: { value: INTENSITY_BOOST }
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });

    this._points = new THREE.Points(geometry, this._material);
    scene.add(this._points);

    this._time = 0;
    this._lastPaletteRef = null;
  }

  // Only rewrites the color buffer when the album's palette actually
  // changes (a reference check, not every frame) - recomputing ~a
  // hundred spots' HSL->RGB every frame would be wasted work for a value
  // that's otherwise constant between tracks.
  //
  // Half the spots (this._usesAlbumColor, a fixed 50/50 split decided
  // once per spot at init) always stay in the yellow band regardless of
  // the cover; the other half take one of the album's real vivid colors
  // (see artwork-palette.js's vividColors - already boosted toward
  // "party" saturation/lightness), the same idea NeonGrid already uses.
  // Saturation/lightness still jitter per spot on the album-color half
  // (using the fixed per-spot seed from init) so it reads as textured
  // rather than ~5 flat color clusters; hue is never jittered there, so
  // the assigned color stays recognizably the album's own.
  _recomputeColors(profile) {
    const vivid = profile.artworkPalette && profile.artworkPalette.vividColors;
    const hasVivid = vivid && vivid.length > 0;
    const tmpColor = new THREE.Color();
    const hsl = { h: 0, s: 0, l: 0 };
    const colorAttr = this._points.geometry.attributes.aColor;
    for (let i = 0; i < this._count; i++) {
      if (hasVivid && this._usesAlbumColor[i]) {
        tmpColor.set(vivid[this._vividSlot[i] % vivid.length]);
        tmpColor.getHSL(hsl);
        const jitter = (this._jitterSeed[i] - 0.5) * 2 * VIVID_JITTER_AMOUNT; // -amount..+amount
        const sat = THREE.MathUtils.clamp(hsl.s * (1 + jitter), 0, 1);
        const light = THREE.MathUtils.clamp(hsl.l * (1 + jitter * 0.6), 0.12, 0.88);
        tmpColor.setHSL(hsl.h, sat, light);
      } else {
        tmpColor.setHSL(this._baseHue[i], this._baseSat[i], this._baseLight[i]);
      }
      colorAttr.array[i * 3] = tmpColor.r;
      colorAttr.array[i * 3 + 1] = tmpColor.g;
      colorAttr.array[i * 3 + 2] = tmpColor.b;
    }
    colorAttr.needsUpdate = true;
  }

  update(deltaTime, profile) {
    if (this._rig) stageRig.update(deltaTime, profile, RIG_OWNER);

    const paused = profile.playbackState !== 'PLAYING';
    if (!paused) this._time += deltaTime;
    this._material.uniforms.uTime.value = this._time;

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._recomputeColors(profile);
    }

    // Same physically-correct world-size -> screen-pixels conversion used
    // by StormLayer, so spots stay a consistent apparent size regardless
    // of canvas resolution/devicePixelRatio.
    const canvasPixelHeight = window.innerHeight * Math.min(window.devicePixelRatio || 1, 2);
    const fovRadians = THREE.MathUtils.degToRad(this._camera.fov);
    this._material.uniforms.uSizeScale.value = canvasPixelHeight / (2 * Math.tan(fovRadians / 2));

    // Subtle track-progress arc, same idea as every other visualizer here -
    // the wall reads slightly more intense mid-track than at the very
    // start/end, without changing which spots are on or their timing.
    const intensity = profile.director.intensity;
    const pulse = profile.director.pulse(BEAT_PULSE_AMOUNT, BEAT_PULSE_DECAY);
    this._material.uniforms.uIntensity.value = INTENSITY_BOOST * (0.75 + intensity * 0.5) * pulse;
  }

  getLightRig() {
    return this._rig;
  }

  dispose() {
    if (this._rig) { stageRig.release(RIG_OWNER); this._rig = null; }
    this._scene.remove(this._points);
    this._points.geometry.dispose();
    this._material.dispose();
  }
}
